import React, {useEffect, useState, useCallback} from "react";
import {FaPhoneAlt, FaRandom} from "react-icons/fa";
import {Route} from "lucide-react";

import JourneyItem from "./JourneyItem";
import FormField from "./FormField";

const fetchCustomerJourney = async (phoneNumber) => {
  const resp = await fetch(
    `${process.env.REACT_APP_AWS_ENDPOINT}/recordings/phonenumber/${phoneNumber}`
  );
  const data = await resp.json();
  if (data.status_code === 200) {
    return data.body.sort(
      (a, b) => new Date(b.start_time) - new Date(a.start_time)
    );
  }
  return [];
};

const updateCustomerInfo = async (payload) => {
  const resp = await fetch(
    `${process.env.REACT_APP_AWS_ENDPOINT}/update-customer-info/customer`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
        Authorization: process.env.REACT_APP_AUTH_TOKEN,
      },
    }
  );
  return resp.json();
};

const ContactCard = ({contactAttributes, queue}) => {
  // 1. Destructure for cleaner access
  const phoneNumber = contactAttributes?.customer_phonenumber ?? " -- ";

  // 2. Data-driven approach for the list items
  const contactInfo = [
    {icon: <FaPhoneAlt />, text: phoneNumber},
    {icon: <FaRandom />, text: queue},
  ];

  return (
    <section className="flex items-center justify-center px-4 py-3">
      <div className="w-full max-w-2xl px-4 py-2 bg-white shadow-2xl rounded-xl">
        <header className="flex mb-3">
          <p className="font-medium text-green-600">On Contact...</p>
        </header>

        <ul className="space-y-3">
          {contactInfo.map((item, index) => (
            <li key={index} className="flex items-center text-gray-500">
              <span className="mr-2 text-[#2ba9cd]" aria-hidden="true">
                {item.icon}
              </span>
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const inputFields = [
  {label: "First Name", name: "firstname", type: "text"},
  {label: "Last Name", name: "lastname", type: "text"},
  {label: "Email", name: "email", type: "email"},
  {label: "Address", name: "address", type: "text"},
  {label: "City", name: "city", type: "text"},
  {label: "State", name: "state", type: "text"},
  {label: "Country", name: "country", type: "text"},
  {label: "Comments", name: "comments", type: "textarea"},
  {label: "Program", name: "program", type: "text"},
];

const Dashboard = ({contactAttributes, queue}) => {
  const [journey, setJourney] = useState([]);
  const [message, setMessage] = useState({text: "", type: ""});
  const [isEdit, setIsEdit] = useState(false);
  const [customer, setCustomer] = useState({});

  const phone = contactAttributes?.customer_phonenumber;

  // Initialize customer data whenever contactAttributes change
  useEffect(() => {
    const fields = [
      "firstname",
      "lastname",
      "email",
      "address",
      "city",
      "state",
      "country",
      "comments",
      "program",
    ];
    const newCustomerData = {};
    fields.forEach((f) => {
      newCustomerData[f] =
        contactAttributes?.[`customer_${f}`] || contactAttributes?.[f] || "--";
    });
    setCustomer(newCustomerData);

    if (phone) {
      fetchCustomerJourney(phone).then(setJourney).catch(console.error);
    } else {
      setJourney([]);
    }
  }, [phone, contactAttributes]);

  const handleSave = async () => {
    try {
      const payload = {
        ...customer,
        customer_phonenumber: phone?.replace("+", ""),
        // Map keys back to API format if necessary
        customer_firstname: customer.firstname,
        customer_lastname: customer.lastname,
      };

      await updateCustomerInfo(payload);
      setMessage({text: "Updated successfully!", type: "success"});
      setIsEdit(false);
    } catch (err) {
      setMessage({text: "Update failed.", type: "error"});
    }
    setTimeout(() => setMessage({text: "", type: ""}), 3000);
  };

  const handleChange = (e) => {
    const {name, value} = e.target;
    setCustomer((prev) => ({...prev, [name]: value}));
  };

  return (
    <div className="flex h-[78vh] bg-gray-100">
      {/* Main Content */}
      <div className="w-3/4 overflow-y-auto p-6 space-y-6">
        {contactAttributes ? (
          <>
            <ContactCard contactAttributes={contactAttributes} queue={queue} />

            <div className="bg-white p-8 rounded-xl shadow-lg max-w-2xl mx-auto">
              {message.text && (
                <div
                  className={`mb-4 p-3 rounded text-center ${
                    message.type === "success"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {message.text}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {inputFields.map((field) => (
                  <FormField
                    key={field.name}
                    field={field}
                    value={customer[field.name]}
                    isEdit={isEdit}
                    onChange={handleChange}
                  />
                ))}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={isEdit ? handleSave : () => setIsEdit(true)}
                  className={`px-6 py-2 rounded-lg font-semibold text-white transition-colors ${
                    isEdit
                      ? "bg-cyan-600 hover:bg-cyan-700"
                      : "bg-gray-600 hover:bg-gray-700"
                  }`}
                >
                  {isEdit ? "Save Changes" : "Edit Details"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-gray-300 rounded-xl bg-white">
            <p className="text-gray-400 font-mono">No active contact</p>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="w-1/4 bg-white border-l p-4 overflow-y-auto">
        <h2 className="text-[#2ba9cd] font-bold flex items-center mb-6">
          <Route className="mr-2" size={20} /> Customer Journey
        </h2>
        <div className="space-y-6">
          {journey.length > 0 ? (
            journey.map((item) => (
              <JourneyItem key={item.ContactId} journey={item} />
            ))
          ) : (
            <p className="text-center text-gray-400 mt-10">No history found</p>
          )}
        </div>
      </div>
    </div>
  );
};
export default Dashboard;
